<script setup>
import { computed, ref } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import WhatsAppTemplateHeader from 'next/message/bubbles/WhatsAppTemplateHeader.vue';
import WhatsAppTemplateButtons from 'next/message/bubbles/WhatsAppTemplateButtons.vue';
import { MESSAGE_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';

const {
  content,
  attachments,
  contentAttributes,
  messageType,
  additionalAttributes,
  inboxId,
} = useMessageContext();

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const renderOriginal = ref(false);

const renderContent = computed(() => {
  if (renderOriginal.value) {
    return content.value;
  }

  if (hasTranslations.value) {
    return translationContent.value;
  }

  return content.value;
});

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length;
});

const whatsAppTemplatePayload = computed(() => {
  const attrs = additionalAttributes?.value ?? {};
  return (
    attrs.templateParams ??
    attrs.template_params ??
    attrs.whatsappTemplate ??
    null
  );
});

const hasTemplateHeader = computed(() => {
  const payload = whatsAppTemplatePayload.value;
  return (
    payload?.components?.some(c => (c.type || '').toLowerCase() === 'header') ??
    false
  );
});

const hasTemplateButtons = computed(() => {
  const payload = whatsAppTemplatePayload.value;
  return (
    payload?.components?.some(c => (c.type || '').toLowerCase() === 'button') ??
    false
  );
});

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};
</script>

<template>
  <BaseBubble class="px-4 py-3" data-bubble-name="text">
    <div class="gap-3 flex flex-col">
      <WhatsAppTemplateHeader
        v-if="hasTemplateHeader && whatsAppTemplatePayload"
        :template-payload="whatsAppTemplatePayload"
      />
      <WhatsAppTemplateButtons
        v-if="hasTemplateButtons && whatsAppTemplatePayload"
        :template-payload="whatsAppTemplatePayload"
        :inbox-id="inboxId"
      />
      <span v-if="isEmpty" class="text-n-slate-11">
        {{ $t('CONVERSATION.NO_CONTENT') }}
      </span>
      <FormattedContent v-if="renderContent" :content="renderContent" />
      <TranslationToggle
        v-if="hasTranslations"
        class="-mt-3"
        :showing-original="renderOriginal"
        @toggle="handleSeeOriginal"
      />
      <AttachmentChips :attachments="attachments" class="gap-2" />
      <template v-if="isTemplate">
        <div
          v-if="contentAttributes.submittedEmail"
          class="px-2 py-1 rounded-lg bg-n-alpha-3"
        >
          {{ contentAttributes.submittedEmail }}
        </div>
      </template>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
